import { tpgIconError } from '@tpg/grammar-web-icons';
import { defineStore } from 'pinia';
import { ref } from 'vue';

import type {
  CreateGeofenceRuleDTO,
  GeofenceDeleteDTO,
  GeofenceRuleDTO,
  GetOperationGeofenceRule200Response,
} from '@nora-monorepo/nora-api-client';
import { stringifyError, useApiStore, useToastStore } from '@nora-monorepo/ui-shared';

export enum GeofenceViewMode {
  Create = 'create',
  Edit = 'edit',
  List = 'list',
}

export const useGeofenceStore = defineStore('geofenceStore', () => {
  const toastStore = useToastStore();
  const isReady = ref(false);
  const isOpen = ref(false);

  const isGeofenceFeatureSelectionActive = ref(false);

  const currentRule = ref<GeofenceRuleDTO | undefined>();

  const api = useApiStore();
  const geofenceRules = ref<GeofenceRuleDTO[]>([]);

  const currentViewMode = ref<GeofenceViewMode | null>(GeofenceViewMode.List);

  const setGeofenceView = (viewMode: GeofenceViewMode): void => {
    currentViewMode.value = viewMode;
  };

  const loadGeofenceRulesForOperation = async (operationId: string): Promise<void> => {
    const response = await api.unwrap(
      api.geofence.listGeofenceRulesByOperation({
        operationId,
      }),
    );

    geofenceRules.value = response.data ?? [];
  };

  const eventAddGeofenceRule = async (rule: GeofenceRuleDTO): Promise<void> => {
    if (!geofenceRules.value.some(({ id }) => id === rule.id)) {
      geofenceRules.value.push(rule);
    }
  };

  const eventUpdateGeofenceRule = async (rule: GeofenceRuleDTO): Promise<void> => {
    const ruleIndex = geofenceRules.value.findIndex(({ id }) => id === rule.id);
    if (ruleIndex !== -1) {
      geofenceRules.value.splice(ruleIndex, 1, rule);
    }
  };

  const eventRemoveGeofenceRule = async (rule: GeofenceDeleteDTO): Promise<void> => {
    const ruleIndex = geofenceRules.value.findIndex(({ id }) => id === rule.id);
    if (ruleIndex !== -1) {
      geofenceRules.value.splice(ruleIndex, 1);
    }
  };

  const eventGeofenceRuleActivate = async (rule: GeofenceRuleDTO): Promise<void> => {
    const ruleIndex = geofenceRules.value.findIndex(({ id }) => id === rule.id);
    if (ruleIndex !== -1) {
      const geofenceRule = geofenceRules.value[ruleIndex];
      geofenceRule.active = true;
    }
  };

  const eventGeofenceRuleDeactivate = async (rule: GeofenceRuleDTO): Promise<void> => {
    const ruleIndex = geofenceRules.value.findIndex(({ id }) => id === rule.id);
    if (ruleIndex !== -1) {
      const geofenceRule = geofenceRules.value[ruleIndex];
      geofenceRule.active = false;
    }
  };

  async function createGeofenceRule(
    operationId: string,
    createGeofenceRuleData: CreateGeofenceRuleDTO,
  ): Promise<void> {
    try {
      await api.geofence.createOperationGeofenceRule({
        operationId,
        createGeofenceRuleDTO: createGeofenceRuleData,
      });
    }
    catch (error) {
      toastStore.addError({
        leadingBadge: {
          label: 'Could not create geofence rule',
          emphasis: 'no-background-high',
          trailingIconTemplate: tpgIconError,
          bottomLabel: stringifyError(error),
        },
      });
    }
  }

  async function deleteGeofenceRule(operationId: string, id: string): Promise<void> {
    try {
      await api.geofence.deleteOperationGeofenceRule({
        operationId,
        id,
      });
    }
    catch (error) {
      toastStore.addError({
        leadingBadge: {
          label: 'Could not delete geofence rule',
          emphasis: 'no-background-high',
          trailingIconTemplate: tpgIconError,
          bottomLabel: stringifyError(error),
        },
      });
      throw new Error('Could not delete geofence rule');
    }
  }

  async function activateGeofenceRule(operationId: string, id: string): Promise<void> {
    try {
      await api.geofence.activateOperationGeofenceRule({
        operationId,
        id,
      });
    }
    catch (error) {
      toastStore.addError({
        leadingBadge: {
          label: 'Could not activate geofence rule',
          emphasis: 'no-background-high',
          trailingIconTemplate: tpgIconError,
          bottomLabel: stringifyError(error),
        },
      });
    }
  }

  async function deactivateGeofenceRule(operationId: string, id: string): Promise<void> {
    try {
      await api.geofence.deactivateOperationGeofenceRule({
        operationId,
        id,
      });
    }
    catch (error) {
      toastStore.addError({
        leadingBadge: {
          label: 'Could not deactivate geofence rule',
          emphasis: 'no-background-high',
          trailingIconTemplate: tpgIconError,
          bottomLabel: stringifyError(error),
        },
      });
    }
  }

  async function getGeofenceRule(operationId: string, id: string): Promise<GetOperationGeofenceRule200Response> {
    try {
      const rule = await api.unwrap(
        api.geofence.getOperationGeofenceRule({
          operationId,
          id,
        }),
      );

      return rule;
    }
    catch (error) {
      toastStore.addError({
        leadingBadge: {
          label: 'Could not retrieve geofence rule',
          emphasis: 'no-background-high',
          trailingIconTemplate: tpgIconError,
          bottomLabel: stringifyError(error),
        },
      });
      throw new Error('Could not retrieve geofence rule');
    }
  }

  async function getGeofenceRuleIfAvailable(
    operationId: string,
    id: string,
  ): Promise<GetOperationGeofenceRule200Response | null> {
    try {
      const ruleResponse = await api.geofence.getOperationGeofenceRule({
        operationId,
        id,
      }, {
        validateStatus: (statusCode: number): boolean => {
          return statusCode === 200 || statusCode === 404;
        },
      });

      if (ruleResponse.status === 404) {
        return null;
      }

      const rule = ruleResponse.data;

      return rule.data ? rule : null;
    }
    catch (error) {
      toastStore.addError({
        leadingBadge: {
          label: 'Could not retrieve geofence rule',
          emphasis: 'no-background-high',
          trailingIconTemplate: tpgIconError,
          bottomLabel: stringifyError(error),
        },
      });

      throw new Error('Could not retrieve geofence rule');
    }
  }

  async function editGeofenceRule(operationId: string, data: GeofenceRuleDTO): Promise<GetOperationGeofenceRule200Response | undefined> {
    try {
      const rule = await api.unwrap(
        api.geofence.updateOperationGeofenceRule({
          operationId,
          id: data.id,
          updateGeofenceRuleDTO: data,
        }),
      );

      const foundRule = geofenceRules.value.find((geofenceRule) => {
        return geofenceRule.id === rule.data?.id;
      });

      if (!foundRule) {
        return;
      }
      if (!rule.data) {
        return;
      }

      const indexOfUpdatedRule = geofenceRules.value.indexOf(foundRule);
      geofenceRules.value.splice(indexOfUpdatedRule, 1, rule.data);

      return rule;
    }
    catch (error) {
      toastStore.addError({
        leadingBadge: {
          label: 'Could not update geofence rule',
          emphasis: 'no-background-high',
          trailingIconTemplate: tpgIconError,
          bottomLabel: stringifyError(error),
        },
      });
      throw new Error('Could not update geofence rule');
    }
  }

  async function bootstrap(): Promise<void> {
    isReady.value = true;
  }

  return {
    currentRule,
    currentViewMode,
    isGeofenceFeatureSelectionActive,
    isOpen,
    isReady,
    geofenceRules,

    eventAddGeofenceRule,
    editGeofenceRule,
    eventRemoveGeofenceRule,
    eventGeofenceRuleActivate,
    eventGeofenceRuleDeactivate,
    eventUpdateGeofenceRule,
    getGeofenceRule,
    getGeofenceRuleIfAvailable,
    setGeofenceView,
    activateGeofenceRule,
    deactivateGeofenceRule,
    loadGeofenceRulesForOperation,
    createGeofenceRule,
    deleteGeofenceRule,
    bootstrap,
  };
});
